import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Time Fields
 * Time tracking and due date fields.
 *
 * `savedViewMembership` mirrors `../saved-views/ticket-saved-views.ts`.
 */
export const timeFields = defineFieldConfigMap({
  // FIELD: due_date
  due_date: {
    type: "due-date", // ✅ Changed from "datetime" to "due-date" to use quick select buttons
    label: "Due Date",
    placeholder: "Select due date",
    autoFill: true, // ✅ Auto-populate with current date
    // Table properties
    sortable: true,
    filterable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "dateRange",
    width: 140,
    minWidth: 120,
    maxWidth: 180,
    icon: "Calendar",
    groupable: true,
    groupingOptions: {
      dateGranularity: "day",
    },
    datetimePresets: {
      enabled: true,
      presets: ["today", "tomorrow", "nextweek", "nextmonth"],
      showCustomRange: true,
    },
    // Clients see when their ticket is due.
    allowedRoles: ["client_user", "wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 1,
    mobileDetailSectionId: "time",
    graphqlSelector: "due_date",
    savedViewMembership: [
      "my-tickets",
      "high-priority",
      "open-tickets",
      "overdue-tickets",
    ],
    /**
     * Tickets "overdue" filter: any ticket whose due_date is before `now`
     * AND whose status isn't already "completed". Moved out of
     * `filter-conversion.ts` so the service no longer branches on entityName.
     */
    toGraphQLFilter: (value, context) => {
      if (value !== "overdue") return null;
      return {
        due_date: { lt: context.now.toISOString() },
        status: { neq: "completed" },
      };
    },
  },

  // FIELD: estimated_hours
  estimated_hours: {
    type: "text",
    label: "Estimated Hours",
    placeholder: "e.g. 2.5",
    validation: {
      min: 0,
      max: 999,
    },
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "numberRange",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Clock",
    groupable: true,
    groupingOptions: {
      numberRanges: {
        ranges: [
          { min: 0, max: 1, label: "0-1 hours" },
          { min: 1, max: 2, label: "1-2 hours" },
          { min: 2, max: 3, label: "2-3 hours" },
          { min: 3, max: 4, label: "3-4 hours" },
          { min: 4, max: 10, label: "4-10 hours" },
          { min: 10, max: null, label: "10+ hours" },
        ],
      },
    },
    // Role-based access control
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "time",
    graphqlSelector: "estimated_hours",
  },

  // FIELD: actual_hours
  actual_hours: {
    type: "text",
    label: "Actual Hours",
    placeholder: "Actual time spent",
    validation: {
      min: 0,
      max: 999,
    },
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "numberRange",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Clock",
    groupable: true,
    groupingOptions: {
      numberRanges: {
        ranges: [
          { min: 0, max: 1, label: "0-1 hours" },
          { min: 1, max: 2, label: "1-2 hours" },
          { min: 2, max: 3, label: "2-3 hours" },
          { min: 3, max: 4, label: "3-4 hours" },
          { min: 4, max: 10, label: "4-10 hours" },
          { min: 10, max: null, label: "10+ hours" },
        ],
      },
    },
    // Internal effort tracking — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "time",
    graphqlSelector: "actual_hours",
  },

  // FIELD: billable_hours
  billable_hours: {
    type: "text",
    label: "Billable Hours",
    placeholder: "Billable time",
    validation: {
      min: 0,
      max: 999,
    },
    // Table properties
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    filterType: "numberRange",
    width: 120,
    minWidth: 100,
    maxWidth: 150,
    icon: "Clock",
    // Internal billing detail — staff only.
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "time",
    graphqlSelector: "billable_hours",
  },
});
